import React, { useEffect, useRef } from 'react'
import type { UseFormReturn } from 'react-hook-form'

import { CheckBoxLabel } from 'components/CheckBoxLabel'

import type { BookingMasterData } from 'repositories/BerthApi/getBookingMasterData/type'

import {
  buildSelectOptions,
  collectSideColumnNames,
  columnNameToFormKey,
  isSelectType,
  sortDisplayableParameters,
  type ImportExportSide,
} from '../../../utils/bookingParameters'
import type {
  EditBookingFormValues,
  EditBookingResolverContext,
} from '../../../utils/editBookingValidation'

import styles from './index.module.css'

type Props = {
  form: UseFormReturn<EditBookingFormValues, EditBookingResolverContext>
  masterData: BookingMasterData | null
  side: ImportExportSide
  isBasicDisabled: boolean
}

const sideFlagKey: Record<ImportExportSide, 'importFlag' | 'exportFlag'> = {
  import: 'importFlag',
  export: 'exportFlag',
}
const sideLabel: Record<ImportExportSide, string> = {
  import: '荷降ろし',
  export: '荷積み',
}

export const CargoSection: React.FC<Props> = ({ form, masterData, side, isBasicDisabled }) => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = form
  const values = watch()

  const flagKey = sideFlagKey[side]
  const flagOn = values[flagKey]

  const previousFlagRef = useRef<boolean>(flagOn)

  // フラグが ON → OFF に切り替わった瞬間に当該サイドのカラムを一括クリア
  useEffect(() => {
    if (!masterData) return
    const wasOn = previousFlagRef.current
    if (wasOn && !flagOn) {
      const cols = collectSideColumnNames(side, masterData.tenantParameters)
      for (const col of cols) {
        const key = columnNameToFormKey(col) as keyof EditBookingFormValues
        setValue(key, '' as never, { shouldDirty: true })
      }
    }
    previousFlagRef.current = flagOn
  }, [flagOn, masterData, side, setValue])

  if (!masterData) return null

  const params = sortDisplayableParameters(
    side === 'import' ? masterData.tenantParameters.import : masterData.tenantParameters.export
  )

  return (
    <div className={styles.subSection}>
      <div className={styles.subSectionHeader}>
        <span className={styles.subSectionLabel}>{sideLabel[side]}</span>
        <CheckBoxLabel label="設定する" disabled={isBasicDisabled} {...register(flagKey)} />
      </div>
      {flagOn && params.length > 0 && (
        <div className={styles.fieldList}>
          {params.map((p) => {
            const formKey = columnNameToFormKey(p.columnName) as keyof EditBookingFormValues
            const current = (values[formKey] as string) ?? ''
            const errorMessage = errors[formKey]?.message as string | undefined

            if (isSelectType({ type: p.type, columnName: p.columnName })) {
              const options = buildSelectOptions(p.columnName, masterData, current)
              return (
                <div key={p.columnName} className={styles.field}>
                  <label className={styles.label} htmlFor={String(formKey)}>
                    {p.displayName}
                  </label>
                  <select
                    id={String(formKey)}
                    className={styles.select}
                    disabled={isBasicDisabled}
                    {...register(formKey)}
                  >
                    <option value=""></option>
                    {options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {errorMessage && <span className={styles.errorText}>{errorMessage}</span>}
                </div>
              )
            }

            return (
              <div key={p.columnName} className={styles.field}>
                <label className={styles.label} htmlFor={String(formKey)}>
                  {p.displayName}
                </label>
                <input
                  id={String(formKey)}
                  type="text"
                  className={styles.input}
                  disabled={isBasicDisabled}
                  {...register(formKey)}
                />
                {errorMessage && <span className={styles.errorText}>{errorMessage}</span>}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
